import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@primer/react';
import { X } from 'lucide-react';
import './appDialog.css';

type DialogOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};
type PromptOptions = DialogOptions & {
  label: string;
  initialValue?: string;
  placeholder?: string;
  maxLength?: number;
};
type DialogRequest = {
  id: number;
  options: DialogOptions | PromptOptions;
  resolve: (value: string | null) => void;
};
type DialogActions = {
  confirmDialog: (options: DialogOptions) => Promise<boolean>;
  promptDialog: (options: PromptOptions) => Promise<string | null>;
};

const DialogContext = createContext<DialogActions | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const queue = useRef<DialogRequest[]>([]);
  const nextId = useRef(0);
  const [active, setActive] = useState<DialogRequest | null>(null);
  const request = useCallback((options: DialogOptions | PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      const item = { id: ++nextId.current, options, resolve };
      queue.current.push(item);
      if (queue.current.length === 1) setActive(item);
    });
  }, []);
  const settle = useCallback((id: number, value: string | null) => {
    if (queue.current[0]?.id !== id) return;
    const item = queue.current.shift();
    setActive(queue.current[0] ?? null);
    item?.resolve(value);
  }, []);
  useEffect(() => {
    return () => {
      queue.current.splice(0).forEach((item) => item.resolve(null));
    };
  }, []);
  const actions = useMemo<DialogActions>(
    () => ({
      confirmDialog: async (options) => (await request(options)) !== null,
      promptDialog: request,
    }),
    [request],
  );
  return (
    <DialogContext.Provider value={actions}>
      {children}
      {active && <AppDialog key={active.id} request={active} onSettle={settle} />}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const value = useContext(DialogContext);
  if (!value) throw new Error('useDialog must be used within DialogProvider.');
  return value;
}

export function AppDialog({
  request,
  onSettle,
}: {
  request: DialogRequest;
  onSettle: (id: number, value: string | null) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const messageId = useId();
  const inputId = useId();
  const { options } = request;
  const prompt = 'label' in options ? options : null;
  const [value, setValue] = useState(prompt?.initialValue ?? '');
  const close = () => onSettle(request.id, null);
  useEffect(() => {
    const node = ref.current;
    const previousFocus = document.activeElement;
    node?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      node?.close();
      document.body.style.overflow = overflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="app-dialog elevation-1"
      role={prompt ? 'dialog' : 'alertdialog'}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={options.message ? messageId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClose={(event) => {
        // StrictMode can close and reopen the same dialog before its queued close event fires.
        if (!event.currentTarget.open) close();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const box = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        )
          close();
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (prompt && !value.trim()) return;
          onSettle(request.id, prompt ? value.trim() : 'confirmed');
        }}
      >
        <div className="app-dialog-heading">
          <h2 id={titleId}>{options.title}</h2>
          <button type="button" className="icon-button" aria-label="Close dialog" onClick={close}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        {options.message && <p id={messageId}>{options.message}</p>}
        {prompt && (
          <label className="app-dialog-field" htmlFor={inputId}>
            <span>{prompt.label}</span>
            <textarea
              id={inputId}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={prompt.placeholder}
              maxLength={prompt.maxLength ?? 2000}
              rows={4}
              required
              autoFocus
            />
          </label>
        )}
        <div className="app-dialog-actions">
          <Button type="button" onClick={close} autoFocus={!prompt}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            type="submit"
            variant={options.danger ? 'danger' : 'primary'}
            disabled={!!prompt && !value.trim()}
          >
            {options.confirmLabel ?? (prompt ? 'Submit' : 'Confirm')}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
